import { forwardRef } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import Check from "lucide-react/dist/esm/icons/check";
import { tv } from "tailwind-variants";

import type { CalendarColor } from "@/types/scheduling";
import { cn } from "@/lib/utils";
import { isNullable } from "@/lib/utils/typeof";

const checkboxColorVariants = tv({
  variants: {
    color: {
      0: "border-calendar-0 data-[state=checked]:bg-calendar-0",
      1: "border-calendar-1 data-[state=checked]:bg-calendar-1",
      2: "border-calendar-2 data-[state=checked]:bg-calendar-2",
      3: "border-calendar-3 data-[state=checked]:bg-calendar-3",
      4: "border-calendar-4 data-[state=checked]:bg-calendar-4",
      5: "border-calendar-5 data-[state=checked]:bg-calendar-5",
      6: "border-calendar-6 data-[state=checked]:bg-calendar-6",
      7: "border-calendar-7 data-[state=checked]:bg-calendar-7",
      8: "border-calendar-8 data-[state=checked]:bg-calendar-8",
      9: "border-calendar-9 data-[state=checked]:bg-calendar-9",
      cancelled: "border-red-400 data-[state=checked]:bg-red-400",
      busy: "border-orange-400 data-[state=checked]:bg-orange-400",
      available: "border-green-500 data-[state=checked]:bg-green-500",
      free: "border-emerald-200 data-[state=checked]:bg-emerald-200",
    },
  },
});

type CheckboxInternalProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> & {
  color?: CalendarColor;
};

export const CheckboxInternal = forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxInternalProps
>(({ className, color, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer border-lines ring-offset-background focus-visible:ring-ring h-4 w-4 shrink-0 rounded-sm border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:text-white",
      !isNullable(color)
        ? checkboxColorVariants({ color })
        : "data-[state=checked]:bg-success data-[state=checked]:text-success-foreground",
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
CheckboxInternal.displayName = "CheckboxInternal";
